const reportCache = new Map();
const reportRequests = new Map();
const timezoneRegistrations = new Map();

const timezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const normalizeScopeKey = (scopeKey) => {
  const normalized = String(scopeKey || '').trim();
  if (!normalized) throw new Error('Reports require an authenticated household scope.');
  return normalized;
};

const requestPathFor = (path) => (
  `${path}${path.includes('?') ? '&' : '?'}timezone=${encodeURIComponent(timezone())}`
);
const cacheKeyFor = (path, scopeKey) => `${normalizeScopeKey(scopeKey)}::${requestPathFor(path)}`;

const readJson = async (response, fallbackMessage) => {
  let data = {};
  try {
    data = await response.json();
  } catch (_error) {
    data = {};
  }
  if (!response.ok) {
    const error = new Error(data?.error || fallbackMessage);
    error.status = response.status;
    throw error;
  }
  return data;
};

const fetchResource = async (authenticatedFetch, path, { force = false, scopeKey } = {}) => {
  const resolvedScope = normalizeScopeKey(scopeKey);
  const requestPath = requestPathFor(path);
  const key = cacheKeyFor(path, resolvedScope);
  const cached = reportCache.get(key);
  if (!force && reportRequests.has(key)) return reportRequests.get(key).promise;

  const headers = {};
  if (cached?.etag) headers['If-None-Match'] = cached.etag;
  const controller = new AbortController();
  const promise = authenticatedFetch(requestPath, { headers, signal: controller.signal })
    .then(async (response) => {
      if (response.status === 304 && cached?.data) return cached.data;
      const data = await readJson(response, 'Couldn’t load Reports right now.');
      // A logout/household switch removes the request before it resolves. Do not
      // let that stale response repopulate the authenticated cache.
      if (reportRequests.get(key)?.promise === promise) {
        reportCache.set(key, { data, etag: response.headers.get('etag') || null });
      }
      return data;
    })
    .finally(() => {
      if (reportRequests.get(key)?.promise === promise) reportRequests.delete(key);
    });

  reportRequests.set(key, { promise, controller, scopeKey: resolvedScope });
  return promise;
};

const peek = (path, scopeKey) => reportCache.get(cacheKeyFor(path, scopeKey))?.data || null;

const clearScope = (scopeKey) => {
  const resolvedScope = normalizeScopeKey(scopeKey);
  const prefix = `${resolvedScope}::`;
  [...reportCache.keys()].filter((key) => key.startsWith(prefix)).forEach((key) => reportCache.delete(key));
  [...reportRequests.entries()].forEach(([key, request]) => {
    if (!key.startsWith(prefix)) return;
    request.controller.abort();
    reportRequests.delete(key);
  });
  timezoneRegistrations.delete(resolvedScope);
};

export const reportsService = {
  async registerTimezone(authenticatedFetch, { scopeKey } = {}) {
    const resolvedScope = normalizeScopeKey(scopeKey);
    const currentTimezone = timezone();
    const existing = timezoneRegistrations.get(resolvedScope);
    if (existing?.timezone === currentTimezone) {
      if (existing.promise) return existing.promise;
      return existing.data;
    }

    const promise = authenticatedFetch('/reports/timezone', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ timezone: currentTimezone }),
    })
      .then((response) => readJson(response, 'Couldn’t update your timezone.'))
      .then((data) => {
        if (timezoneRegistrations.get(resolvedScope)?.promise === promise) {
          timezoneRegistrations.set(resolvedScope, { timezone: currentTimezone, data });
        }
        return data;
      })
      .catch((error) => {
        if (timezoneRegistrations.get(resolvedScope)?.promise === promise) {
          timezoneRegistrations.delete(resolvedScope);
        }
        throw error;
      });
    timezoneRegistrations.set(resolvedScope, { timezone: currentTimezone, promise });
    return promise;
  },

  getArchive(authenticatedFetch, options) {
    return fetchResource(authenticatedFetch, '/reports?limit=24', options);
  },

  getReport(authenticatedFetch, monthKey, options) {
    return fetchResource(authenticatedFetch, `/reports/${encodeURIComponent(monthKey)}`, options);
  },

  getPrompt(authenticatedFetch, options) {
    return fetchResource(authenticatedFetch, '/reports/prompt', options);
  },

  async recordPromptAction(authenticatedFetch, monthKey, action, { scopeKey } = {}) {
    const resolvedScope = normalizeScopeKey(scopeKey);
    const response = await authenticatedFetch(
      `/reports/prompt/${encodeURIComponent(monthKey)}/${encodeURIComponent(action)}`,
      { method: 'POST' }
    );
    const data = await readJson(response, 'Couldn’t update your report prompt.');
    reportCache.delete(cacheKeyFor('/reports/prompt', resolvedScope));
    return data;
  },

  peekArchive(scopeKey) {
    return peek('/reports?limit=24', scopeKey);
  },

  peekReport(monthKey, scopeKey) {
    return peek(`/reports/${encodeURIComponent(monthKey)}`, scopeKey);
  },

  clearMonth(monthKey, scopeKey) {
    reportCache.delete(cacheKeyFor(`/reports/${encodeURIComponent(monthKey)}`, scopeKey));
    reportCache.delete(cacheKeyFor('/reports?limit=24', scopeKey));
  },

  clearScope,

  clearAll() {
    reportRequests.forEach((request) => request.controller.abort());
    reportCache.clear();
    reportRequests.clear();
    timezoneRegistrations.clear();
  },
};

export default reportsService;
